import React, { useId } from 'react';

interface ProgressRingProps {
  /** 0–100, or null when there's no data yet */
  percent: number | null;
  label: string;
  icon?: string;
  /** solid stroke color (used when `gradient` is not provided) */
  color?: string;
  /** [from, to] for a gradient stroke — overrides `color` */
  gradient?: [string, string];
  size?: number;
  strokeWidth?: number;
}

const ProgressRing: React.FC<ProgressRingProps> = ({
  percent,
  label,
  icon,
  color = '#6366F1',
  gradient,
  size = 132,
  strokeWidth = 12,
}) => {
  const gid = useId();
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const hasData = percent !== null && !Number.isNaN(percent);
  const clamped = hasData ? Math.max(0, Math.min(100, percent as number)) : 0;
  const offset = circumference - (clamped / 100) * circumference;
  const stroke = gradient ? `url(#${gid})` : color;
  const glow = gradient ? gradient[1] : color;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          {gradient && (
            <defs>
              <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={gradient[0]} />
                <stop offset="100%" stopColor={gradient[1]} />
              </linearGradient>
            </defs>
          )}
          {/* Track */}
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#EEF0F4" strokeWidth={strokeWidth} />
          {/* Progress arc */}
          {hasData && (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={stroke}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              style={{ transition: 'stroke-dashoffset 0.7s ease', filter: `drop-shadow(0 2px 6px ${glow}66)` }}
            />
          )}
        </svg>
        {/* Center label */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {icon && <span className="text-2xl leading-none mb-0.5">{icon}</span>}
          {hasData ? (
            <span className="text-2xl font-extrabold font-display" style={{ color: glow }}>
              {Math.round(clamped)}%
            </span>
          ) : (
            <span className="text-xs text-gray-400 text-center px-2">No data yet</span>
          )}
        </div>
      </div>
      <span className="text-sm font-bold text-gray-700">{label}</span>
    </div>
  );
};

export default ProgressRing;
